// What the sync screen shows, from what the sync and the scan queue report:
// pure, so the screen's words and its decisions (when it may go on, when it
// goes on by itself) are tested without React.
import type { ScanProgress } from '@chess/api-scan';
import type { SyncState } from '@chess/api-sync';

export type StepStatus = 'waiting' | 'running' | 'done' | 'failed';

export interface StepView {
  status: StepStatus;
  /** One line under the step's title: progress, the result, or the error. */
  detail: string;
}

export interface SyncView {
  /** The import from chess.com. */
  importStep: StepView;
  /** The engine's scan of the games not scanned yet. */
  scanStep: StepView;
  /**
   * The user may go on while the scan continues: the import is over and the
   * latest game is read (it is what the home shows, so entering before it
   * is read would show an empty home), or the import failed, or there is
   * no game to wait for.
   */
  mayContinue: boolean;
  /** Everything is over: the screen goes on by itself. */
  complete: boolean;
}

export interface SyncInput {
  sync: SyncState;
  scan?: ScanProgress;
  /** "~3 min" for the games left, from the queue's estimate; undefined while nothing scans. */
  estimate?: string;
  /** The newest game held: none, waiting for its scan, or scanned. */
  latest: 'none' | 'pending' | 'scanned';
}

const plural = (n: number, word: string) => `${n} ${n === 1 ? word : `${word}s`}`;

export function syncView({ sync, scan, estimate, latest }: SyncInput): SyncView {
  const importStep = importStepOf(sync);
  const scanStep = scanStepOf(sync, scan, estimate, latest);
  return {
    importStep,
    scanStep,
    mayContinue: sync.phase === 'error' || (sync.phase === 'done' && latest !== 'pending'),
    complete: sync.phase === 'done' && scanStep.status === 'done',
  };
}

function importStepOf(sync: SyncState): StepView {
  switch (sync.phase) {
    case 'idle':
      return { status: 'running', detail: 'Connecting to chess.com' };
    case 'importing':
      return {
        status: 'running',
        detail: `${sync.month ? `Reading ${sync.month}` : 'Reading your archives'} · ${plural(sync.imported ?? 0, 'new game')}`,
      };
    case 'done': {
      if (sync.note) return { status: 'done', detail: sync.note };
      const n = sync.imported ?? 0;
      if (!n) return { status: 'done', detail: 'No new games since the last sync' };
      return { status: 'done', detail: `${plural(n, 'new game')} from the last 30 days` };
    }
    case 'error':
      return { status: 'failed', detail: `Could not reach chess.com: ${sync.error ?? 'unknown error'}` };
  }
}

function scanStepOf(sync: SyncState, scan: ScanProgress | undefined, estimate: string | undefined, latest: SyncInput['latest']): StepView {
  if (sync.phase === 'idle' || sync.phase === 'importing') return { status: 'waiting', detail: 'After the import' };
  if (sync.phase === 'error') return { status: 'waiting', detail: 'The games already here are scanned in the background' };
  if (scan?.running) {
    const at = Math.min(scan.total, scan.done + 1);
    const first = latest === 'pending' ? 'Your latest game first · ' : '';
    return { status: 'running', detail: `${first}Scanning game ${at} of ${scan.total}${estimate ? ` · ${estimate}` : ''}` };
  }
  return { status: 'done', detail: 'Every game is scanned' };
}
